/**
 * AppSidebar -- shared layout primitive for all sidebar areas (Chat, Code, Admin).
 *
 * Pure layout component with zero business logic. Each area mounts this
 * and passes area-specific children via slots.
 *
 * Layout contract:
 *   <nav> outer flex column (h-full)
 *     +-- header        (shrink-0, optional, border-b border-border-subtle)
 *     +-- subHeader     (shrink-0, optional, border-b border-border-subtle)
 *     +-- scrollable    (flex-1 min-h-0 overflow-y-auto pb-3) <-- ONLY this scrolls
 *     +-- footer        (shrink-0, optional, border-t border-border-subtle)
 *     +-- areaSwitcher  (shrink-0, optional, border-t border-accent-500, px-2 py-2)
 *
 * Width / responsive / collapse behaviour is owned by the HOST (CodeShellPage,
 * ChatPage, AdminLayout) via the wrapping <aside>. This primitive only owns
 * vertical layout and token application.
 */

import type { CSSProperties, ReactNode } from 'react';
import { cn } from '@/utils/cn';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AppSidebarProps {
  /** Top non-scrollable block (logo + brand controls, or area title + 'New' button). */
  header?: ReactNode;
  /** Optional second non-scrollable block under header (search, tabs, filters). */
  subHeader?: ReactNode;
  /** Main body -- the area-specific list. Renders inside flex-1 min-h-0 overflow-y-auto pb-3. */
  scrollable: ReactNode;
  /** Optional area-specific block ABOVE the AreaSwitcher (e.g. workspace status, bulk actions). */
  footer?: ReactNode;
  /** Pinned-bottom area switcher. Default consumer: <AreaSwitcher />. */
  areaSwitcher?: ReactNode;
  /** Accessible label for the outer <nav>. Caller passes a localised string. */
  ariaLabel: string;
  /** Optional className appended to the outer <nav>. Used by hosts to override width. */
  className?: string;
  /** Optional inline styles (e.g. for dynamic resize width). */
  style?: CSSProperties;
  /** When true, signals collapsed state to children via data-collapsed attribute. */
  collapsed?: boolean;
  /** E2E selector attribute. Always emitted as data-sidebar="app". */
  'data-sidebar'?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AppSidebar({
  header,
  subHeader,
  scrollable,
  footer,
  areaSwitcher,
  ariaLabel,
  className,
  style,
  collapsed = false,
  'data-sidebar': dataSidebar = 'app',
}: AppSidebarProps) {
  return (
    <nav
      data-sidebar={dataSidebar}
      data-collapsed={collapsed ? 'true' : 'false'}
      aria-label={ariaLabel}
      style={style}
      className={cn(
        'flex flex-col h-full bg-sidebar-bg border-r border-border-default',
        className,
      )}
    >
      {/* Header: brand / title / primary CTA. shrink-0. */}
      {header && (
        <div className="shrink-0 border-b border-border-subtle">
          {header}
        </div>
      )}

      {/* SubHeader: search, tabs, filters. shrink-0. */}
      {subHeader && (
        <div className="shrink-0 border-b border-border-subtle">
          {subHeader}
        </div>
      )}

      {/*
        Scrollable middle. THE ONLY scrolling region in the sidebar.
        min-h-0 is REQUIRED: without it, flex-1 inherits min-height:auto
        and child content pushes pinned blocks below the viewport.
      */}
      <div
        className="flex-1 min-h-0 overflow-y-auto pb-3"
        data-sidebar-scroll
      >
        {scrollable}
      </div>

      {/* Area-specific pinned footer (workspace status, bulk actions, user card). shrink-0. */}
      {footer && (
        <div className="shrink-0 border-t border-border-subtle">
          {footer}
        </div>
      )}

      {/* Standardised area switcher -- ALWAYS LAST. shrink-0. */}
      {areaSwitcher && (
        <div className="shrink-0 border-t border-accent-500 px-2 py-2">
          {areaSwitcher}
        </div>
      )}
    </nav>
  );
}
